import type { PartnerResponseDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import profilePhoto from "Shared/Assets/Images/Profiles/user-1.jpg";
import { BlankCard } from "Shared/Components/Cards/BlankCard";
import { DescriptionOutlined, MailOutline, Phone } from "@mui/icons-material";
import { Avatar, Box, Grid2, Stack, Typography } from "@mui/material";
import type { FC } from "react";

const avatarStyle = {
	borderRadius: "50%",
	width: "100px",
	height: "100px",
	border: "4px solid #fff",
	alignSelf: "center",
};

type PartnerTabHeaderProps = {
	partner: PartnerResponseDto;
};

const partnerResource = Resources.base.partner.shared;

export const PartnerTabHeader: FC<PartnerTabHeaderProps> = ({ partner }) => {
	const { t } = useResource();

	return (
		<BlankCard sx={{ overflow: "visible", marginTop: "50px" }}>
			<Grid2 container spacing={0} justifyContent="center" alignItems="center">
				<Grid2
					size={{ xs: 12, sm: 6, md: 4 }}
					sx={{
						order: {
							xs: "2",
							sm: "2",
							md: "1",
						},
					}}
				>
					<Stack
						direction="row"
						textAlign="center"
						justifyContent="flex-start"
						gap={6}
						m={3}
					>
						<Box>
							<Typography color="text.secondary">
								<DescriptionOutlined fontSize="small" />
							</Typography>
							<Typography variant="h4" fontWeight="600">
								{partner?.orderCount ?? 0}
							</Typography>
							<Typography color="textSecondary" variant="h6" fontWeight={400}>
								{t(partnerResource.orders)}
							</Typography>
						</Box>
					</Stack>
				</Grid2>
				<Grid2
					size={{ xs: 12, sm: 12, md: 4 }}
					sx={{
						order: {
							xs: "1",
							sm: "1",
							md: "2",
						},
					}}
				>
					<Box
						display="flex"
						alignItems="center"
						textAlign="center"
						justifyContent="center"
						sx={{
							mt: "-85px",
						}}
					>
						<Box display="flex" alignItems="center" flexDirection="column">
							<Avatar src={profilePhoto} alt="Profile image" sx={avatarStyle} />
							<Box mt={1}>
								<Typography variant="h5">
									{`${partner.partner.name} ${partner.partner.middleName && `${partner.partner.middleName} `}${partner.partner.surname}`}
								</Typography>
								<Typography color="textSecondary" variant="h6">
									{partner.partner.verticals &&
									partner.partner.verticals.length > 0
										? partner.partner.verticals
												.map(
													(vertical) =>
														vertical.name.charAt(0).toUpperCase() +
														vertical.name.slice(1),
												)
												.join(", ")
										: ""}
								</Typography>
							</Box>
						</Box>
					</Box>
				</Grid2>

				<Grid2
					size={{ xs: 12, sm: 6, md: 4 }}
					sx={{
						order: {
							xs: "3",
							sm: "3",
							md: "3",
						},
					}}
				>
					<Stack
						direction="row"
						gap={3}
						m={3}
						textAlign="center"
						justifyContent="center"
					>
						<Box>
							<a
								href={`tel:${partner.partner.phone}`}
								style={{ textDecoration: "none", color: "inherit" }}
							>
								<Typography color="text.secondary">
									<Phone fontSize="small" />
								</Typography>
								<Typography variant="subtitle1" fontWeight="600">
									{partner.partner.phone}
								</Typography>
							</a>
							<Typography color="textSecondary" variant="h6" fontWeight={400}>
								{t(partnerResource.phone)}
							</Typography>
						</Box>
						<Box>
							<a
								href={`mailto:${partner.partner.email}`}
								style={{ textDecoration: "none", color: "inherit" }}
							>
								<Typography color="text.secondary">
									<MailOutline fontSize="small" />
								</Typography>
								<Typography variant="subtitle1" fontWeight="600">
									{partner.partner.email}
								</Typography>
							</a>
							<Typography color="textSecondary" variant="h6" fontWeight={400}>
								{t(partnerResource.email)}
							</Typography>
						</Box>
					</Stack>
				</Grid2>
			</Grid2>
		</BlankCard>
	);
};
